import React from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { isNil } from 'lodash'
import { DataItem } from 'scrivito'

import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { DataClassName } from '@/Data/const'
import { useDataItemReferenceHandler } from '@/hooks/useDataItemReferenceHandler'
import {
  getDataItemTitle,
  getEntityIconElementByDataClass,
  getEntityIconElementByEntity,
} from '@/utils/dataClassMap'
import { getDisplayValue } from '@/utils/getDisplayValue'

interface KanbanItemProps {
  item: DataItem
  schema: DataItem
  preview?: boolean
  onOpenItemDrawer?: (item: DataItem) => void
}

export const KanbanItem: React.FC<KanbanItemProps> = ({
  item,
  schema,
  preview,
  onOpenItemDrawer,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id(), data: { previewItem: item } })

  const style = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const previewStyle = preview
    ? {
        cursor: 'grabbing',
        transform: 'scale(105%) rotate(-3deg)',
        zIndex: 5,
      }
    : undefined

  let ItemContent = KanbanItemDefault

  switch (item.dataClassName()) {
    case DataClassName.LEAD:
      ItemContent = KanbanItemLead
      break
    case DataClassName.OPPORTUNITY:
      ItemContent = KanbanItemOpportunity
      break
    default:
      ItemContent = KanbanItemDefault
      break
  }

  return (
    <div
      key={item.id()}
      className="card card-kanban"
      ref={setNodeRef}
      style={{
        ...style,
        ...previewStyle,
      }}
      {...attributes}
      {...listeners}
    >
      <ItemContent
        item={item}
        schema={schema}
        onOpenItemDrawer={onOpenItemDrawer}
      />
    </div>
  )
}

const KanbanItemOpportunity: React.FC<KanbanItemProps> = ({
  item,
  schema,
  onOpenItemDrawer,
}) => {
  const { add, get } = useDataItemReferenceHandler()
  return (
    <>
      <div className="card-body">
        <div className="jr-list-item mb-2">
          <div className="jr-list-group shrink">{getIcon(item)}</div>
          <div className="jr-list-group grow">
            <div className="d-flex justify-content-between" data-no-dnd="true">
              <StringText
                data-no-dnd="true"
                variant="link"
                dataItem={item}
                className="h5 mb-0 underline-on-hover-only"
              >
                {getDataItemTitle(item)}
              </StringText>
              <Button
                variant="icon"
                icon="chevron-right"
                data-no-dnd="true"
                onClick={() => {
                  onOpenItemDrawer?.(item)
                }}
              />
            </div>
            {!isNil(item.get('customer')) && (
              <p className="text-small text-meta mb-0">
                {getDisplayValue({
                  rawValue: item.get('customer'),
                  key: 'customer',
                  schema,
                  get,
                  add,
                })}
              </p>
            )}
          </div>
        </div>
        <div className="jr-list-item">
          {!isNil(item.get('totalPrice')) && (
            <div className="jr-list-group grow">
              <span className="my-auto text-small text-meta">
                {getDisplayValue({
                  rawValue: item.get('totalPrice'),
                  key: 'totalPrice',
                  schema,
                })}{' '}
                {getDisplayValue({
                  rawValue: item.get('totalPriceCurrency'),
                  key: 'totalPriceCurrency',
                  schema,
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const KanbanItemLead: React.FC<KanbanItemProps> = ({
  item,
  onOpenItemDrawer,
}) => {
  return (
    <>
      <div className="card-body">
        <div className="jr-list-item mb-2">
          <div className="jr-list-group shrink">{getIcon(item)}</div>
          <div className="jr-list-group grow">
            <div className="d-flex justify-content-between" data-no-dnd="true">
              <StringText
                data-no-dnd="true"
                variant="link"
                dataItem={item}
                className="h5 mb-0 underline-on-hover-only"
              >
                {getDataItemTitle(item)}
              </StringText>
              <Button
                variant="icon"
                icon="chevron-right"
                data-no-dnd="true"
                onClick={() => {
                  onOpenItemDrawer?.(item)
                }}
              />
            </div>
            {!isNil(item.get('email')) && (
              <p className="text-small text-meta mb-0">
                {getDisplayValue({
                  rawValue: item.get('email'),
                })}
              </p>
            )}
          </div>
        </div>
        <div className="jr-list-item"></div>
      </div>
    </>
  )
}

const KanbanItemDefault: React.FC<KanbanItemProps> = ({
  item,
  onOpenItemDrawer,
}) => {
  return (
    <>
      <div className="card-body">
        <div className="jr-list-item mb-2">
          <div className="jr-list-group shrink">{getIcon(item)}</div>
          <div className="jr-list-group grow">
            <div className="d-flex justify-content-between" data-no-dnd="true">
              <StringText
                data-no-dnd="true"
                variant="link"
                dataItem={item}
                className="h5 mb-0 underline-on-hover-only"
              >
                {getDataItemTitle(item)}
              </StringText>
              <Button
                variant="icon"
                icon="chevron-right"
                data-no-dnd="true"
                onClick={() => {
                  onOpenItemDrawer?.(item)
                }}
              />
            </div>
          </div>
        </div>
        <div className="jr-list-item"></div>
      </div>
    </>
  )
}

const getIcon = (dataItem: DataItem) => {
  const getIconElement = dataItem.get('entity')
    ? getEntityIconElementByEntity
    : getEntityIconElementByDataClass
  return getIconElement(dataItem, {
    avatarSize: 'lg',
    className: 'me-3',
  })
}
